/*
 * MySiteCare
 *
 * Copyright © 2026 Jeanjen Sisoy Luberio
 * doing business as MySiteCare Website Application Services.
 * All rights reserved.
 *
 * DTI Business Name No. 7934250
 * https://mysitecare.eu
 *
 * This file contains original proprietary MySiteCare source code,
 * except for third-party components identified only under their
 * respective licenses.
 */
import { useEffect, useState } from "react";
import SiteDashboardShell, { useSiteDashboard } from "@/components/customer/SiteDashboardShell";
import { fetchSiteProducts, createSiteProduct, fetchSiteProduct, patchSiteProduct, createSiteVariant, adjustSiteStock, fetchSiteInventoryMovements, fetchSiteInventorySummary } from "@/lib/api";

function money(minor) { return `₱${((minor || 0) / 100).toLocaleString("en-PH", { minimumFractionDigits: 2 })}`; }

function SummaryStrip({ summary }) {
  if (!summary) return null;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
      {[["Total Products", summary.total_products], ["Active", summary.active_products], ["Total Stock Units", summary.total_stock_units],
        ["Low Stock", summary.low_stock_count], ["Out of Stock", summary.out_of_stock_count]].map(([label, value]) => (
        <div key={label} className="rounded-xl border border-[#E2E8F0] bg-white p-3.5">
          <p className="text-xs text-[#94A3B8]">{label}</p>
          <p className="mt-1 text-xl font-bold font-display text-[#0F172A]">{value}</p>
        </div>
      ))}
    </div>
  );
}

function AddProductForm({ token, slug, onAdded }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", sku: "", price_minor: "", stock_quantity: "", category: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  if (!open) return <button onClick={() => setOpen(true)} className="mb-5 rounded-lg bg-[#0F172A] px-4 py-2 text-sm font-medium text-white">Add Product</button>;
  return (
    <div className="mb-5 rounded-2xl border border-[#E2E8F0] bg-white p-5">
      <div className="grid gap-3 sm:grid-cols-2">
        <input placeholder="Product name" value={form.name} onChange={set("name")} className="rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm" />
        <input placeholder="SKU (optional)" value={form.sku} onChange={set("sku")} className="rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm" />
        <input placeholder="Category (optional)" value={form.category} onChange={set("category")} className="rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm" />
        <input type="number" placeholder="Price (₱)" value={form.price_minor} onChange={set("price_minor")} className="rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm" />
        <input type="number" placeholder="Starting stock" value={form.stock_quantity} onChange={set("stock_quantity")} className="rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm" />
      </div>
      <div className="mt-3 flex gap-2">
        <button disabled={!form.name} onClick={() => createSiteProduct(token, slug, {
          name: form.name, sku: form.sku || undefined, category: form.category || undefined,
          price_minor: Math.round(Number(form.price_minor || 0) * 100), stock_quantity: Number(form.stock_quantity || 0), status: "active",
        }).then(() => { setForm({ name: "", sku: "", price_minor: "", stock_quantity: "", category: "" }); setOpen(false); onAdded(); })}
          className="rounded-lg bg-[#0F172A] px-4 py-2 text-sm font-medium text-white disabled:opacity-40">Save</button>
        <button onClick={() => setOpen(false)} className="rounded-lg border border-[#E2E8F0] px-4 py-2 text-sm">Cancel</button>
      </div>
    </div>
  );
}

function ProductDetail({ token, slug, productId, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [movements, setMovements] = useState(null);
  const [adjustQty, setAdjustQty] = useState("");
  const [adjustReason, setAdjustReason] = useState("");
  const [variantName, setVariantName] = useState("");

  const load = () => {
    fetchSiteProduct(token, slug, productId).then(setDetail).catch(() => {});
    fetchSiteInventoryMovements(token, slug, { product_id: productId }).then(setMovements).catch(() => {});
  };
  useEffect(load, [token, slug, productId]);

  if (!detail) return null;
  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center bg-black/30 p-4 overflow-y-auto" onClick={onClose}>
      <div className="mt-10 w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="product-detail-panel">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold font-display text-[#0F172A]">{detail.name}</h3>
          <button onClick={onClose} className="text-sm text-[#94A3B8] hover:text-[#0F172A]">Close</button>
        </div>
        <p className="mt-1 text-sm text-[#475569]">{money(detail.price_minor)} · SKU {detail.sku || "—"} · Stock: <strong>{detail.stock_quantity}</strong></p>

        <label htmlFor="product-status" className="mt-4 block text-xs font-medium uppercase text-[#475569]">Status</label>
        <select id="product-status" value={detail.status} onChange={(e) => patchSiteProduct(token, slug, productId, { status: e.target.value }).then(() => { load(); onChanged(); })}
          className="mt-1 w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm bg-white">
          <option value="draft">Draft</option><option value="active">Active</option><option value="out_of_stock">Out of stock</option><option value="archived">Archived</option>
        </select>

        <p className="mt-4 text-xs font-medium uppercase text-[#475569]">Adjust stock</p>
        <div className="mt-1 flex gap-2">
          <input type="number" placeholder="+/- qty" value={adjustQty} onChange={(e) => setAdjustQty(e.target.value)} className="w-24 rounded-lg border border-[#E2E8F0] px-2 py-2 text-sm" />
          <input placeholder="Reason" value={adjustReason} onChange={(e) => setAdjustReason(e.target.value)} className="flex-1 rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm" />
          <button disabled={!adjustQty || !adjustReason} onClick={() => adjustSiteStock(token, slug, productId, { quantity_change: Number(adjustQty), reason: adjustReason }).then(() => { setAdjustQty(""); setAdjustReason(""); load(); onChanged(); })}
            className="rounded-lg bg-[#0F172A] px-3 py-2 text-sm font-medium text-white disabled:opacity-40">Apply</button>
        </div>

        <p className="mt-4 text-xs font-medium uppercase text-[#475569]">Variants</p>
        {(detail.variants || []).map((v) => <p key={v.id} className="text-sm text-[#475569]">{v.name} — stock {v.stock_quantity}{v.sku ? ` · ${v.sku}` : ""}</p>)}
        <div className="mt-1 flex gap-2">
          <input placeholder="New variant (e.g. Size 7)" value={variantName} onChange={(e) => setVariantName(e.target.value)} className="flex-1 rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm" />
          <button disabled={!variantName} onClick={() => createSiteVariant(token, slug, productId, { name: variantName, stock_quantity: 0 }).then(() => { setVariantName(""); load(); })}
            className="rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm">Add</button>
        </div>

        <p className="mt-4 text-xs font-medium uppercase text-[#475569]">Inventory history</p>
        {(movements?.items || []).length === 0 ? <p className="mt-1 text-sm text-[#94A3B8]">No stock movements yet.</p> : (
          <ul className="mt-1 space-y-1 text-sm text-[#475569]">
            {movements.items.map((m) => <li key={m.id}>{new Date(m.created_at).toLocaleDateString()} — {m.movement_type.replace(/_/g, " ")}: {m.quantity_change > 0 ? "+" : ""}{m.quantity_change} ({m.quantity_before} → {m.quantity_after}){m.reason ? ` · ${m.reason}` : ""}</li>)}
          </ul>
        )}
      </div>
    </div>
  );
}

export default function SiteInventory() {
  const dashboard = useSiteDashboard();
  const { token, slug } = dashboard;
  const [products, setProducts] = useState(null);
  const [summary, setSummary] = useState(null);
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState(null);

  const load = () => {
    if (!token) return;
    fetchSiteProducts(token, slug, search ? { search } : {}).then(setProducts).catch(() => {});
    fetchSiteInventorySummary(token, slug).then(setSummary).catch(() => {});
  };
  useEffect(load, [token, slug, search]);

  return (
    <SiteDashboardShell active="inventory" dashboard={dashboard}>
      <h2 className="text-lg font-bold font-display text-[#0F172A] mb-4">Inventory</h2>
      <SummaryStrip summary={summary} />
      <AddProductForm token={token} slug={slug} onAdded={load} />
      <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products…" className="mb-3 w-full max-w-xs rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm" />

      <div className="rounded-2xl border border-[#E2E8F0] bg-white overflow-hidden overflow-x-auto">
        {!products ? (
          <p className="p-6 text-sm text-[#475569]">Loading…</p>
        ) : products.items.length === 0 ? (
          <p className="p-6 text-sm text-[#94A3B8]">No products yet. Add your first product to start tracking inventory.</p>
        ) : (
          <table className="w-full min-w-[520px] text-left text-sm">
            <thead className="bg-[#F8FAFC] text-xs uppercase tracking-wide text-[#94A3B8]"><tr><th className="px-4 py-2.5">Name</th><th className="px-4 py-2.5">Price</th><th className="px-4 py-2.5">Stock</th><th className="px-4 py-2.5">Status</th></tr></thead>
            <tbody className="divide-y divide-[#E2E8F0]">
              {products.items.map((p) => (
                <tr key={p.id} onClick={() => setOpenId(p.id)} className="cursor-pointer hover:bg-[#F8FAFC]">
                  <td className="px-4 py-3 font-medium text-[#0F172A]">{p.name}</td>
                  <td className="px-4 py-3">{money(p.price_minor)}</td>
                  <td className={`px-4 py-3 ${p.stock_quantity <= p.low_stock_threshold ? "text-amber-700 font-semibold" : ""}`}>{p.stock_quantity}</td>
                  <td className="px-4 py-3 capitalize">{p.status.replace(/_/g, " ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {openId && <ProductDetail token={token} slug={slug} productId={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </SiteDashboardShell>
  );
}
